import { supabase } from "./supabase";

/**
 * SU- og synkspørsmål: redaktørens flate.
 *
 * SU er stand up — deltakeren står i gangen og svarer på tre raske spørsmål.
 * En SYNK er det lange intervjuet. To situasjoner, én liste. Se
 * `docs/01-domene.md`.
 *
 * REDAKTØR SKRIVER, REGI STILLER. Spørsmålene hører til produksjonen, ikke
 * til brukeren: det er samme liste som dukker opp i appen, og regi kan legge
 * til sine egne i felt. Det er motsatt av hurtigknappene, som er per bruker —
 * knappene er ergonomi, spørsmålene er redaksjonelt innhold.
 *
 * `deltakerId` null betyr GENERELT. Spørsmålet stilles til alle, og ligger
 * øverst på hver deltakers liste i appen under sin egen tittel.
 */

export interface Deltaker {
  readonly id: string;
  readonly navn: string;
  readonly forkortelse: string;
}

export interface Spoersmaal {
  readonly id: string;
  readonly deltakerId: string | null;
  readonly tekst: string;
  readonly sortOrder: number;
  /**
   * Naar spoersmaalet ble skrevet. Avgjoer om det er blitt en pickup.
   *
   * Null paa rader fra foer 26.10.2026 — da vet vi det ikke, og da flyttes
   * de ikke. Se `erPickup` i packages/shared/synk.
   */
  readonly skrevetMs: number | null;
  /**
   * Opptaksdagen det ble skrevet paa. Staar som knagg i rapporten, slik at
   * klipperen finner materialet spoersmaalet hoerer til.
   */
  readonly opptaksdag: string | null;
}

export interface SuData {
  readonly deltakere: readonly Deltaker[];
  readonly spoersmaal: readonly Spoersmaal[];
}

export const TOM: SuData = { deltakere: [], spoersmaal: [] };

/**
 * Henter begge listene.
 *
 * Kaster ikke: uten klient eller uten nett er flata tom, og det er en
 * tilstand skjermen må kunne vise — ikke en feil som skal felle siden.
 */
export async function hent(produksjonId: string): Promise<SuData> {
  if (supabase === null) return TOM;

  const [d, q] = await Promise.all([
    supabase
      .from("participant")
      .select("id, name, abbreviation")
      .eq("production_id", produksjonId)
      .order("abbreviation", { ascending: true }),
    supabase
      .from("su_question")
      .select("id, participant_id, text, sort_order, created_at, shoot_day")
      .eq("production_id", produksjonId)
      .order("sort_order", { ascending: true }),
  ]);
  if (d.error !== null || q.error !== null) return TOM;

  return {
    deltakere: (d.data ?? []).map((r) => ({
      id: String(r.id),
      navn: String(r.name),
      forkortelse: String(r.abbreviation),
    })),
    spoersmaal: (q.data ?? []).map((rad) => {
      const r = rad as unknown as Record<string, unknown>;
      return {
        id: String(r["id"]),
        deltakerId: r["participant_id"] == null ? null : String(r["participant_id"]),
        tekst: String(r["text"]),
        sortOrder: Number(r["sort_order"]),
        skrevetMs: r["created_at"] == null ? null : Date.parse(String(r["created_at"])),
        opptaksdag: r["shoot_day"] == null ? null : String(r["shoot_day"]),
      };
    }),
  };
}

/** Nytt spørsmål. Returnerer raden slik den ble, eller null om den feilet. */
export async function leggTil(
  produksjonId: string,
  deltakerId: string | null,
  tekst: string,
  sortOrder: number,
  /*
   * Opptaksdagen det skrives paa, og det er DAGEN DEN SOM SKRIVER STAAR I —
   * ikke dagen det er.
   *
   * Redaktoer gaar tilbake tre dager for aa jobbe med en historie som ble
   * fortalt da, ser at det trengs en synk for aa fortelle den ferdig, og
   * skriver spoersmaalet der. Knaggen skal si dag 4, ikke dagen i dag: det
   * er dag 4 klipperen skal lete i. `oekt.shootDay` paa nettflata ER den
   * valgte dagen, saa kallerne sender den uten aa gjoere noe saerskilt.
   *
   * `created_at` kan ikke svare paa dette, og det er ikke bare fordi et
   * skift kan gaa over midnatt — det er fordi dagen man JOBBER i og dagen
   * det ER, er to ulike ting paa nettflata. Presisert 26.10.2026.
   */
  opptaksdag: string,
): Promise<Spoersmaal | null> {
  if (supabase === null) return null;
  const { data, error } = await supabase
    .from("su_question")
    .insert({
      production_id: produksjonId,
      participant_id: deltakerId,
      text: tekst,
      sort_order: sortOrder,
      shoot_day: opptaksdag,
    })
    .select("id, participant_id, text, sort_order, created_at, shoot_day")
    .single();
  if (error !== null || data === null) return null;
  const r = data as unknown as Record<string, unknown>;
  return {
    id: String(r["id"]),
    deltakerId: r["participant_id"] == null ? null : String(r["participant_id"]),
    tekst: String(r["text"]),
    sortOrder: Number(r["sort_order"]),
    skrevetMs: r["created_at"] == null ? null : Date.parse(String(r["created_at"])),
    opptaksdag: r["shoot_day"] == null ? null : String(r["shoot_day"]),
  };
}

export async function endre(id: string, tekst: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("su_question").update({ text: tekst }).eq("id", id);
  return error === null;
}

/**
 * Fjerner et spørsmål.
 *
 * SLETTES FYSISK, og det er riktig her: spørsmålet er en plan, ikke en
 * hendelse. Det som faktisk BLE spurt om står i loggen som en SYNK-post, og
 * den røres ikke — `su_answer` peker på spørsmålet, så en rad som er brukt
 * tar svaret med seg. Historikken ligger i loggposten, ikke i lista.
 */
export async function fjern(id: string): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("su_question").delete().eq("id", id);
  return error === null;
}
